import Vue from 'vue';
import VueFileAgent from 'vue-file-agent';
import VueFileAgentStyles from 'vue-file-agent/dist/vue-file-agent.css';
import VueRouter from 'vue-router';
import ActivateAccount from '../views/ActivateAccount.vue';
import AnalysisGroupDetail from '../views/AnalysisGroupDetail.vue';
import AnalysisGroupsList from '../views/AnalysisGroupsList.vue';
import AnalysisQueue from '../views/AnalysisQueue.vue';
import AnalysisRunDetail from '../views/AnalysisRunDetail.vue';
import AnalysisRunsList from '../views/AnalysisRunsList.vue';
import CandidatesList from '../views/CandidatesList.vue';
import CompetenciesList from '../views/CompetenciesList.vue';
import ComponentsList from '../views/ComponentsList.vue';
import ContextsList from '../views/ContextsList.vue';
import CvInfo from '../views/CvInfo.vue';
import EngineChangelog from '../views/EngineChangelog.vue';
import Home from '../views/Home.vue';
import InterviewAnalysisTypesList from '../views/InterviewAnalysisTypesList.vue';
import InterviewTypesList from '../views/InterviewTypesList.vue';
import JobsList from '../views/JobsList.vue';
import LandingPage from '../views/LandingPage.vue';
import PositionsList from '../views/PositionsList.vue';
import ResetPassword from '../views/ResetPassword.vue';
import UploadCV from '../views/UploadCV.vue';
import UsersList from '../views/UsersList.vue';
import VirtualProfilerList from '../views/VirtualProfilerList.vue';
import VisibleBehavioursList from '../views/VisibleBehavioursList.vue';
import WaitingRoom from '../views/WaitingRoom.vue';
import WaitingRoomCandidate from '../views/WaitingRoomCandidate.vue';
import WaitingRoomCandidateAvatar from '../views/WaitingRoomCandidateAvatar.vue';
import AvatarInterview from '@/views/AvatarInterview.vue';
import ErrorPage from '@/views/ErrorPage.vue';
import Interview from '@/views/Interview.vue';
import InterviewAnalysis from '@/views/InterviewAnalysis.vue';
import ManualVideoAnalysisList from '@/views/ManualVideoAnalysisList.vue';
import SharedAnalysis from '@/views/SharedAnalysis.vue';
import CoherenceEvaluation from '@/views/CoherenceEvaluation.vue';

import '@mdi/font/css/materialdesignicons.min.css';

import moment from 'moment';
import utils from '@/utils/utils';
import featureFlags from '@/config/featureFlags';

Vue.use(VueRouter);
Vue.use(VueFileAgent);

// Avoid NavigationDuplicated when navigating to the same route (e.g. multiple 401 handlers pushing to '/')
const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }
  return originalPush.call(this, location).catch((err) => {
    if (err.name !== 'NavigationDuplicated') throw err;
  });
};

const originalReplace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject);
  }
  return originalReplace.call(this, location).catch((err) => {
    if (err.name !== 'NavigationDuplicated') throw err;
  });
};

Vue.filter('formatDate', function (value) {
  if (value) {
    return moment(String(value)).format('YYYY-MM-DD HH:mm');
  }
});

Vue.filter('slice20Characters', function (value) {
  if (value) {
    return value.length >= 20 ? value.slice(0, 20) + '...' : value;
  }
});

Vue.filter('slice40Characters', function (value) {
  if (value) {
    return value.length >= 40 ? value.slice(0, 40) + '...' : value;
  }
});

Vue.filter('slice100Characters', function (value) {
  if (value) {
    return value.length >= 100 ? value.slice(0, 100) + '...' : value;
  }
});

function getCurrentUser() {
  return localStorage.getItem('user');
}

const routes = [
  { path: '/', name: 'landing page', component: LandingPage },
  {
    path: '/activate-account',
    name: 'activate account',
    component: ActivateAccount,
  },
  { path: '/reset-password', name: 'reset password', component: ResetPassword },
  {
    path: '/home',
    name: 'home',
    component: Home,
    meta: { requiresAuth: true },
  },
  {
    path: '/jobs',
    name: 'jobs list',
    component: JobsList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/contexts',
    name: 'contexts list',
    component: ContextsList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/competencies',
    name: 'competencies list',
    component: CompetenciesList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/visible-behaviours',
    name: 'visible behaviours list',
    component: VisibleBehavioursList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/positions',
    name: 'positions list',
    component: PositionsList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/interview-types',
    name: 'interview types list',
    component: InterviewTypesList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/components',
    name: 'components list',
    component: ComponentsList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/interview-analysis-types',
    alias: '/interview-analysis-configs',
    name: 'interview analysis config list',
    component: InterviewAnalysisTypesList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/virtual-profilers',
    name: 'virtual profilers list',
    component: VirtualProfilerList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/candidates',
    name: 'candidates list',
    component: CandidatesList,

    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/users',
    name: 'users list',
    component: UsersList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/upload-cv',
    name: 'upload cv',
    component: UploadCV,
    meta: { requiresAuth: true },
  },
  {
    path: '/cv-info',
    name: 'cv info',
    component: CvInfo,
    meta: { requiresAuth: true },
  },
  {
    path: '/waiting-room/:positionId',
    name: 'waiting room',
    component: WaitingRoom,
  },
  {
    path: '/waiting-room-candidate',
    name: 'waiting room candidate',
    component: WaitingRoomCandidate,
  },
  {
    path: '/waiting-room-candidate-avatar',
    name: 'waiting room candidate avatar',
    component: WaitingRoomCandidateAvatar,
  },
  {
    path: '/error',
    name: 'error',
    component: ErrorPage,
  },
  { path: '/interview', name: 'interview', component: Interview },
  {
    path: '/avatar-interview',
    name: 'avatar interview',
    component: AvatarInterview,
  },
  {
    path: '/interview-analysis/:id',
    name: 'interview analysis',
    component: InterviewAnalysis,
    props: { isManualAnalysis: false },
    meta: {
      requiresAuth: true,
      backTo: '/candidates',
    },
  },
  {
    path: '/coherence-analysis/:id',
    name: 'manual analysis',
    component: InterviewAnalysis,
    props: { isManualAnalysis: true },
    meta: {
      requiresAuth: true,
      backTo: '/manual-video-analysis',
    },
  },
  {
    path: '/manual-analysis/:id',
    redirect: (to) => `/coherence-analysis/${to.params.id}`,
  },
  {
    path: '/shared/:token',
    name: 'shared analysis',
    component: SharedAnalysis,
    // No requiresAuth — public route
  },
  {
    path: '/manual-video-analysis',
    name: 'manual video analysis',
    component: ManualVideoAnalysisList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/coherence-evaluation',
    name: 'coherence evaluation',
    component: CoherenceEvaluation,
    meta: {
      requiresAuth: true,
      backTo: '/manual-video-analysis',
    },
  },
  {
    path: '/analysis-queue',
    name: 'analysis queue',
    component: AnalysisQueue,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/analysis-groups',
    name: 'analysis groups',
    component: AnalysisGroupsList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/analysis-groups/:id',
    name: 'analysis group detail',
    component: AnalysisGroupDetail,
    meta: {
      requiresAuth: true,
      backTo: '/analysis-groups',
    },
  },
  {
    path: '/runs',
    name: 'analysis runs',
    component: AnalysisRunsList,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
  {
    path: '/runs/:id',
    name: 'analysis run detail',
    component: AnalysisRunDetail,
    meta: {
      requiresAuth: true,
      backTo: '/runs',
    },
  },
  {
    // Not gated by a flag: a read-only list of engine versions bothers nobody.
    path: '/engine-changelog',
    name: 'engine changelog',
    component: EngineChangelog,
    meta: {
      requiresAuth: true,
      backTo: '/home',
    },
  },
];

// const notAllowed = ["cv info", "upload cv"];
const notAllowed = [];

const router = new VueRouter({
  mode: 'history',
  routes: utils.config.preProd
    ? routes
    : routes.filter((route) => {
        return notAllowed.indexOf(route.name) === -1;
      }),
});

// Route names blocked while their feature flag is disabled (CDTI demo scope).
// Keeps deep links (e.g. /coherence-analysis/123, /shared/<token>) from
// bypassing the hidden UI. Flip the flag in config/featureFlags.js to restore.
const FEATURE_DISABLED_ROUTE_NAMES = new Set();
if (!featureFlags.manualSemantica) {
  // "NEXT Semantica" manual video analysis flow (list, result view, evaluation).
  ['manual video analysis', 'manual analysis', 'coherence evaluation'].forEach((name) =>
    FEATURE_DISABLED_ROUTE_NAMES.add(name)
  );
}
if (!featureFlags.shareReport) {
  FEATURE_DISABLED_ROUTE_NAMES.add('shared analysis');
}
// Analysis control screens (queue and groups) stay hidden until their flag is on.
if (!featureFlags.analysisQueue) {
  FEATURE_DISABLED_ROUTE_NAMES.add('analysis queue');
}
if (!featureFlags.analysisGroups) {
  ['analysis groups', 'analysis group detail'].forEach((name) =>
    FEATURE_DISABLED_ROUTE_NAMES.add(name)
  );
}
// Folder Runs (/runs and /runs/:id). /engine-changelog stays reachable on purpose.
if (!featureFlags.folderRuns) {
  ['analysis runs', 'analysis run detail'].forEach((name) =>
    FEATURE_DISABLED_ROUTE_NAMES.add(name)
  );
}

router.beforeEach((to, from, next) => {
  if (to.name && FEATURE_DISABLED_ROUTE_NAMES.has(to.name)) {
    next('/home');
    return;
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const currentUser = getCurrentUser();

  if (requiresAuth && !currentUser) next('/');
  else next();
});

export default router;
